/**
 * ============================================================================
 * TableNodeView — NodeView React da tabela do KB (estilo GitBook/Notion).
 *
 * Fornece:
 *  - Scroll horizontal interno para tabelas largas.
 *  - Botões "+" na borda direita/inferior para adicionar coluna/linha ao fim.
 *  - HANDLES INLINE por coluna e por linha (aparecem no hover) com Popover
 *    contextual (Inserir antes/depois, Selecionar, Remover, etc.) que respeita
 *    a coluna/linha em que o mouse está — resolvido via posAtDOM da célula
 *    alvo, garantindo que o comando não caia sempre na primeira coluna.
 *  - Botões "+" finos entre colunas/linhas (estilo Notion) para inserção
 *    precisa no divisor exato.
 *  - Highlight visual sutil da coluna/linha em foco.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v3.0.0 - MutationObserver p/ ressincronizar tableRef
 *            após adicionar/remover linhas ou colunas; zona de segurança
 *            (padding) na hover-area para o mouse alcançar os handles sem
 *            "sair" da tabela; hover pelo padding preserva último col/row;
 *            botões "+" entre colunas e entre linhas; recompute do hover em
 *            cada mutação DOM.
 *   RELEASE: 2026-07-08 v2.0.0 - Handles por coluna/linha com Popover contextual
 *            (Inserir esq/dir, acima/abaixo, Remover, Selecionar, Mesclar/Dividir,
 *            Alternar cabeçalho da coluna). Remove ambiguidade do 6-pontinhos.
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial (scroll + botões hover).
 * ============================================================================
 */
import {
  NodeViewWrapper,
  NodeViewContent,
  type NodeViewProps,
} from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import { CellSelection } from "@tiptap/pm/tables";
import { TextSelection } from "@tiptap/pm/state";
import {
  Plus,
  MoreHorizontal,
  MoreVertical,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  ArrowDownToLine,
  Trash2,
  Merge,
  Split,
  Heading,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";

// ---------------------------------------------------------------------------
// Helpers de scroll (evita que focus() role a coluna do editor até o final)
// ---------------------------------------------------------------------------
function captureScroll(editor: Editor) {
  const el = editor.view.dom.closest("[data-kb-scroll]") as HTMLElement | null;
  return { el, top: el?.scrollTop ?? 0, left: el?.scrollLeft ?? 0 };
}
function restoreScroll(snap: ReturnType<typeof captureScroll>) {
  const apply = () => {
    if (!snap.el) return;
    snap.el.scrollTop = snap.top;
    snap.el.scrollLeft = snap.left;
  };
  apply();
  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });
}
function preserve(editor: Editor, fn: () => void) {
  const snap = captureScroll(editor);
  fn();
  restoreScroll(snap);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function TableNodeView({ editor, getPos, node }: NodeViewProps) {
  const editable = editor?.isEditable ?? true;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);

  // Coluna e linha atualmente sob o mouse (para posicionar handles + highlight)
  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  const [hoveredRow, setHoveredRow] = useState<number | null>(null);
  const [colHandleRect, setColHandleRect] = useState<{ left: number; width: number } | null>(null);
  const [rowHandleRect, setRowHandleRect] = useState<{ top: number; height: number } | null>(null);
  const [colMenuOpen, setColMenuOpen] = useState(false);
  const [rowMenuOpen, setRowMenuOpen] = useState(false);

  // "+" entre colunas/linhas — geometria de cada divisor
  const [colDividers, setColDividers] = useState<Array<{ left: number; index: number }>>([]);
  const [rowDividers, setRowDividers] = useState<Array<{ top: number; index: number }>>([]);

  // ------------------- Localiza <table> real + observa mutações -------------------
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    const sync = () => {
      tableRef.current = wrap.querySelector("table");
      recomputeDividers();
    };
    sync();

    // Observa alterações do subtree (adicionar/remover linhas ou colunas
    // pelo ProseMirror recria o <table>). Sem isso, tableRef fica apontando
    // pro nó antigo e o mousemove seguinte não acha células.
    const observer = new MutationObserver(() => {
      sync();
    });
    observer.observe(wrap, { childList: true, subtree: true });

    // Recomputa dividers em resize da janela também
    const onResize = () => recomputeDividers();
    window.addEventListener("resize", onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recomputa a posição visual (left/top) dos divisores "+" entre colunas e linhas.
  const recomputeDividers = useCallback(() => {
    const table = tableRef.current;
    const wrap = wrapRef.current;
    if (!table || !wrap) {
      setColDividers([]);
      setRowDividers([]);
      return;
    }
    const wrapRect = wrap.getBoundingClientRect();

    const firstRow = table.rows[0];
    if (firstRow) {
      const arr: Array<{ left: number; index: number }> = [];
      for (let i = 0; i < firstRow.cells.length; i++) {
        const rect = firstRow.cells[i].getBoundingClientRect();
        // divisor à DIREITA da célula i: insere após i (colIdx = i, addColumnAfter)
        arr.push({ left: rect.right - wrapRect.left, index: i });
      }
      setColDividers(arr);
    }

    const rowArr: Array<{ top: number; index: number }> = [];
    for (let i = 0; i < table.rows.length; i++) {
      const cell = table.rows[i].cells[0];
      if (!cell) continue;
      const rect = cell.getBoundingClientRect();
      rowArr.push({ top: rect.bottom - wrapRect.top, index: i });
    }
    setRowDividers(rowArr);
  }, []);

  // ------------------- Detecção de coluna/linha sob o mouse -------------------
  const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
    if (!editable || colMenuOpen || rowMenuOpen) return;
    const table = tableRef.current;
    const wrap = wrapRef.current;
    if (!table || !wrap) return;

    const wrapRect = wrap.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Coluna: usa as células da primeira linha como referência
    const firstRow = table.rows[0];
    if (firstRow && firstRow.cells.length > 0) {
      let colIdx: number | null = null;
      let rect: { left: number; width: number } | null = null;
      for (let i = 0; i < firstRow.cells.length; i++) {
        const cellRect = firstRow.cells[i].getBoundingClientRect();
        if (x >= cellRect.left && x <= cellRect.right) {
          colIdx = i;
          rect = { left: cellRect.left - wrapRect.left, width: cellRect.width };
          break;
        }
      }
      // Só zera se o mouse estiver COMPLETAMENTE fora do range horizontal da
      // tabela. Se estiver na "zona de padding" acima da tabela, preserva
      // último hoveredCol para que o handle não pisque ao mover o mouse até ele.
      if (colIdx !== null) {
        setHoveredCol(colIdx);
        setColHandleRect(rect);
      } else {
        const firstCellRect = firstRow.cells[0].getBoundingClientRect();
        const lastCellRect = firstRow.cells[firstRow.cells.length - 1].getBoundingClientRect();
        if (x < firstCellRect.left || x > lastCellRect.right) {
          setHoveredCol(null);
          setColHandleRect(null);
        }
      }
    }

    // Linha: usa a primeira célula de cada linha
    let rowIdx: number | null = null;
    let rowRect: { top: number; height: number } | null = null;
    for (let i = 0; i < table.rows.length; i++) {
      const cell = table.rows[i].cells[0];
      if (!cell) continue;
      const cellRect = cell.getBoundingClientRect();
      if (y >= cellRect.top && y <= cellRect.bottom) {
        rowIdx = i;
        rowRect = { top: cellRect.top - wrapRect.top, height: cellRect.height };
        break;
      }
    }
    if (rowIdx !== null) {
      setHoveredRow(rowIdx);
      setRowHandleRect(rowRect);
    } else {
      // preserva hoveredRow se o mouse está na zona de padding à esquerda
      const firstCellRect = table.rows[0]?.cells[0]?.getBoundingClientRect();
      const lastCellRect = table.rows[table.rows.length - 1]?.cells[0]?.getBoundingClientRect();
      if (!firstCellRect || !lastCellRect || y < firstCellRect.top || y > lastCellRect.bottom) {
        setHoveredRow(null);
        setRowHandleRect(null);
      }
    }
  }, [editable, colMenuOpen, rowMenuOpen]);

  const handleMouseLeave = useCallback(() => {
    if (colMenuOpen || rowMenuOpen) return;
    setHoveredCol(null);
    setHoveredRow(null);
    setColHandleRect(null);
    setRowHandleRect(null);
  }, [colMenuOpen, rowMenuOpen]);

  // ------------------- Foco em célula alvo (col/row) via DOM -------------------
  /**
   * Foca DENTRO da célula (rowIdx, colIdx) da tabela.
   * Necessário porque os comandos do prosemirror-tables (addColumnBefore,
   * deleteRow, etc.) só funcionam com seleção dentro de uma célula
   * (isInTable === true).
   *
   * Usamos posAtDOM na célula DOM real, o que respeita rowspan/colspan.
   */
  const focusCell = (rowIdx: number, colIdx: number) => {
    const table = tableRef.current;
    if (!table) return false;
    const row = table.rows[rowIdx];
    if (!row) return false;
    const cell = row.cells[colIdx] ?? row.cells[row.cells.length - 1];
    if (!cell) return false;
    try {
      const pos = editor.view.posAtDOM(cell, 0);
      const $pos = editor.state.doc.resolve(pos);
      const sel = TextSelection.near($pos);
      editor.view.dispatch(editor.state.tr.setSelection(sel));
      editor.view.focus();
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Seleciona a COLUNA inteira via CellSelection.
   */
  const selectColumn = (colIdx: number) => {
    const table = tableRef.current;
    if (!table) return false;
    const firstCell = table.rows[0]?.cells[colIdx];
    const lastCell = table.rows[table.rows.length - 1]?.cells[colIdx];
    if (!firstCell || !lastCell) return false;
    try {
      const anchor = editor.view.posAtDOM(firstCell, 0);
      const head = editor.view.posAtDOM(lastCell, 0);
      const $anchor = editor.state.doc.resolve(anchor);
      const $head = editor.state.doc.resolve(head);
      const sel = CellSelection.colSelection($anchor, $head);
      editor.view.dispatch(editor.state.tr.setSelection(sel as any));
      editor.view.focus();
      return true;
    } catch {
      return false;
    }
  };

  const selectRow = (rowIdx: number) => {
    const table = tableRef.current;
    if (!table) return false;
    const row = table.rows[rowIdx];
    if (!row) return false;
    const firstCell = row.cells[0];
    const lastCell = row.cells[row.cells.length - 1];
    if (!firstCell || !lastCell) return false;
    try {
      const anchor = editor.view.posAtDOM(firstCell, 0);
      const head = editor.view.posAtDOM(lastCell, 0);
      const $anchor = editor.state.doc.resolve(anchor);
      const $head = editor.state.doc.resolve(head);
      const sel = CellSelection.rowSelection($anchor, $head);
      editor.view.dispatch(editor.state.tr.setSelection(sel as any));
      editor.view.focus();
      return true;
    } catch {
      return false;
    }
  };

  // ------------------- Ações -------------------
  const runOnColumn = (colIdx: number, action: (chain: any) => any) => {
    preserve(editor, () => {
      if (!focusCell(0, colIdx)) return;
      action(editor.chain().focus()).run();
      setColMenuOpen(false);
    });
  };

  const runOnRow = (rowIdx: number, action: (chain: any) => any) => {
    preserve(editor, () => {
      if (!focusCell(rowIdx, 0)) return;
      action(editor.chain().focus()).run();
      setRowMenuOpen(false);
    });
  };

  // Botões de adicionar ao FIM
  const focusLastCell = () => {
    const table = node;
    const pos = typeof getPos === "function" ? getPos() : null;
    if (pos == null) return false;
    const target = pos + table.nodeSize - 3;
    editor.chain().focus().setTextSelection(Math.max(pos + 1, target)).run();
    return true;
  };
  const handleAppendColumn = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    preserve(editor, () => {
      if (!focusLastCell()) return;
      editor.chain().focus().addColumnAfter().run();
    });
  };
  const handleAppendRow = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    preserve(editor, () => {
      if (!focusLastCell()) return;
      editor.chain().focus().addRowAfter().run();
    });
  };

  const hasCellSelection = editor.state.selection instanceof CellSelection;

  return (
    <NodeViewWrapper
      as="div"
      className="kb-table-wrap"
      data-type="table"
    >
      <div
        ref={wrapRef}
        className="kb-table-hover-area"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <div className="kb-table-scroll">
          <NodeViewContent as={"table" as any} />
        </div>

        {/* Highlight visual da coluna/linha hover */}
        {editable && hoveredCol != null && colHandleRect && !colMenuOpen && !rowMenuOpen && (
          <div
            className="kb-col-highlight"
            style={{ left: colHandleRect.left, width: colHandleRect.width }}
            aria-hidden
          />
        )}
        {editable && hoveredRow != null && rowHandleRect && !colMenuOpen && !rowMenuOpen && (
          <div
            className="kb-row-highlight"
            style={{ top: rowHandleRect.top, height: rowHandleRect.height }}
            aria-hidden
          />
        )}

        {/* Handle de COLUNA (aparece acima da coluna hover) */}
        {editable && hoveredCol != null && colHandleRect && (
          <Popover open={colMenuOpen} onOpenChange={setColMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                contentEditable={false}
                className="kb-col-handle"
                title="Opções de coluna"
                style={{ left: colHandleRect.left, width: colHandleRect.width }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              side="top"
              sideOffset={4}
              className="w-56 p-1"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <ColMenu
                colIdx={hoveredCol}
                hasCellSelection={hasCellSelection}
                onInsertLeft={() => runOnColumn(hoveredCol, (c) => c.addColumnBefore())}
                onInsertRight={() => runOnColumn(hoveredCol, (c) => c.addColumnAfter())}
                onSelect={() => {
                  preserve(editor, () => {
                    selectColumn(hoveredCol);
                    setColMenuOpen(false);
                  });
                }}
                onToggleHeader={() => runOnColumn(hoveredCol, (c) => c.toggleHeaderColumn())}
                onMerge={() => runOnColumn(hoveredCol, (c) => c.mergeCells())}
                onSplit={() => runOnColumn(hoveredCol, (c) => c.splitCell())}
                onDelete={() => runOnColumn(hoveredCol, (c) => c.deleteColumn())}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Handle de LINHA (aparece à esquerda da linha hover) */}
        {editable && hoveredRow != null && rowHandleRect && (
          <Popover open={rowMenuOpen} onOpenChange={setRowMenuOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                contentEditable={false}
                className="kb-row-handle"
                title="Opções de linha"
                style={{ top: rowHandleRect.top, height: rowHandleRect.height }}
                onMouseDown={(e) => e.preventDefault()}
              >
                <MoreVertical className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              side="left"
              sideOffset={4}
              className="w-56 p-1"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onCloseAutoFocus={(e) => e.preventDefault()}
            >
              <RowMenu
                rowIdx={hoveredRow}
                hasCellSelection={hasCellSelection}
                onInsertAbove={() => runOnRow(hoveredRow, (c) => c.addRowBefore())}
                onInsertBelow={() => runOnRow(hoveredRow, (c) => c.addRowAfter())}
                onSelect={() => {
                  preserve(editor, () => {
                    selectRow(hoveredRow);
                    setRowMenuOpen(false);
                  });
                }}
                onToggleHeader={() => runOnRow(hoveredRow, (c) => c.toggleHeaderRow())}
                onMerge={() => runOnRow(hoveredRow, (c) => c.mergeCells())}
                onSplit={() => runOnRow(hoveredRow, (c) => c.splitCell())}
                onDelete={() => runOnRow(hoveredRow, (c) => c.deleteRow())}
              />
            </PopoverContent>
          </Popover>
        )}

        {/* Divisores "+" entre COLUNAS (aparecem no hover fino do divisor) */}
        {editable && colDividers.map((d) => (
          <button
            key={`c-div-${d.index}`}
            type="button"
            contentEditable={false}
            className="kb-col-insert-between"
            title="Inserir coluna aqui"
            style={{ left: d.left }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              preserve(editor, () => {
                if (!focusCell(0, d.index)) return;
                editor.chain().focus().addColumnAfter().run();
              });
            }}
          >
            <span className="kb-col-insert-line" aria-hidden />
            <span className="kb-col-insert-dot" aria-hidden>
              <Plus className="h-3 w-3" />
            </span>
          </button>
        ))}

        {/* Divisores "+" entre LINHAS */}
        {editable && rowDividers.map((d) => (
          <button
            key={`r-div-${d.index}`}
            type="button"
            contentEditable={false}
            className="kb-row-insert-between"
            title="Inserir linha aqui"
            style={{ top: d.top }}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              preserve(editor, () => {
                if (!focusCell(d.index, 0)) return;
                editor.chain().focus().addRowAfter().run();
              });
            }}
          >
            <span className="kb-row-insert-line" aria-hidden />
            <span className="kb-row-insert-dot" aria-hidden>
              <Plus className="h-3 w-3" />
            </span>
          </button>
        ))}
      </div>

      {/* Atalhos: adicionar coluna/linha AO FINAL */}
      {editable && (
        <>
          <button
            type="button"
            contentEditable={false}
            className="kb-table-add-col"
            title="Adicionar coluna ao final"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAppendColumn}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            contentEditable={false}
            className="kb-table-add-row"
            title="Adicionar linha ao final"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAppendRow}
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </NodeViewWrapper>
  );
}

// ---------------------------------------------------------------------------
// Sub-componentes (menus dos handles)
// ---------------------------------------------------------------------------
interface ColMenuProps {
  colIdx: number;
  hasCellSelection: boolean;
  onInsertLeft: () => void;
  onInsertRight: () => void;
  onSelect: () => void;
  onToggleHeader: () => void;
  onMerge: () => void;
  onSplit: () => void;
  onDelete: () => void;
}

function MenuButton({
  icon: Icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${
        danger ? "text-destructive hover:text-destructive" : ""
      }`}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
}

function ColMenu({
  hasCellSelection,
  onInsertLeft,
  onInsertRight,
  onSelect,
  onToggleHeader,
  onMerge,
  onSplit,
  onDelete,
}: ColMenuProps) {
  return (
    <div className="flex flex-col">
      <MenuButton icon={ArrowLeftToLine} label="Inserir coluna à esquerda" onClick={onInsertLeft} />
      <MenuButton icon={ArrowRightToLine} label="Inserir coluna à direita" onClick={onInsertRight} />
      <MenuButton icon={MoreHorizontal} label="Selecionar coluna" onClick={onSelect} />
      <div className="my-1 h-px bg-border" />
      <MenuButton icon={Heading} label="Alternar cabeçalho da coluna" onClick={onToggleHeader} />
      {hasCellSelection && (
        <>
          <MenuButton icon={Merge} label="Mesclar células" onClick={onMerge} />
          <MenuButton icon={Split} label="Dividir célula" onClick={onSplit} />
        </>
      )}
      <div className="my-1 h-px bg-border" />
      <MenuButton icon={Trash2} label="Remover coluna" onClick={onDelete} danger />
    </div>
  );
}

interface RowMenuProps {
  rowIdx: number;
  hasCellSelection: boolean;
  onInsertAbove: () => void;
  onInsertBelow: () => void;
  onSelect: () => void;
  onToggleHeader: () => void;
  onMerge: () => void;
  onSplit: () => void;
  onDelete: () => void;
}

function RowMenu({
  hasCellSelection,
  onInsertAbove,
  onInsertBelow,
  onSelect,
  onToggleHeader,
  onMerge,
  onSplit,
  onDelete,
}: RowMenuProps) {
  return (
    <div className="flex flex-col">
      <MenuButton icon={ArrowUpToLine} label="Inserir linha acima" onClick={onInsertAbove} />
      <MenuButton icon={ArrowDownToLine} label="Inserir linha abaixo" onClick={onInsertBelow} />
      <MenuButton icon={MoreVertical} label="Selecionar linha" onClick={onSelect} />
      <div className="my-1 h-px bg-border" />
      <MenuButton icon={Heading} label="Alternar cabeçalho da linha" onClick={onToggleHeader} />
      {hasCellSelection && (
        <>
          <MenuButton icon={Merge} label="Mesclar células" onClick={onMerge} />
          <MenuButton icon={Split} label="Dividir célula" onClick={onSplit} />
        </>
      )}
      <div className="my-1 h-px bg-border" />
      <MenuButton icon={Trash2} label="Remover linha" onClick={onDelete} danger />
    </div>
  );
}
